import React from 'react'

const RefundPolicyPage = () => {
  return (
    <main className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <header className="mb-10 sm:mb-14">
          <p className="text-sm font-semibold text-emerald-600 mb-2 uppercase tracking-wide">
            Refund Policy
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-900 mb-4 leading-tight">
            How refunds and cancellations work on Obule
          </h1>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl font-light">
            This Refund Policy explains when and how you may be eligible for a refund for tickets,
            offers, and other purchases made through the Obule platform. Because Obule works with
            independent centers and event organizers, some refund rules are determined by the
            organizers themselves. We aim to provide a clear, fair, and consistent experience for
            both attendees and organizers.
          </p>
        </header>

        <div className="space-y-10">
          {/* 1. General principles */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              1. General refund principles
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              Unless otherwise stated for a specific event or offer, all sales made through Obule
              are considered final at the time of purchase. However, many organizers choose to
              offer flexible refund or exchange policies, and we may assist in facilitating these
              where possible. Your eligibility for a refund depends on the organizer&apos;s rules,
              the timing of your request, and applicable laws in your jurisdiction.
            </p>
          </section>

          {/* 2. Organizer-specific policies */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              2. Organizer‑specific refund policies
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              Each center or event organizer may define their own refund terms, which are typically
              displayed on the event or offer page, at checkout, or in the confirmation email. These
              may include:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-slate-600 font-light">
              <li>Refunds allowed up to a certain number of days or hours before the event.</li>
              <li>Partial refunds or credits for future events instead of full refunds.</li>
              <li>Non‑refundable tickets or offers, except in the case of event cancellation.</li>
              <li>Special conditions for discounted or promotional tickets and offers.</li>
            </ul>
            <p className="mt-3 text-base text-slate-600 leading-relaxed font-light">
              By completing a purchase, you agree to the specific refund terms set by the
              organizer. We recommend reviewing those terms carefully before confirming your order.
            </p>
          </section>

          {/* 3. Event cancellations and significant changes */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              3. Event cancellations and significant changes
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              If an event is cancelled, postponed, or significantly changed (for example, a major
              change to date, venue, or headlining performers), the organizer is generally
              responsible for deciding and communicating the available options. These may include
              full refunds, partial refunds, or credits for a rescheduled event or future purchase.
              Where the organizer instructs us to issue refunds, we will process them on their
              behalf back to the original payment method where feasible.
            </p>
          </section>

          {/* 4. How to request a refund */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">4. How to request a refund</h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              To request a refund, you can typically use one of the following channels:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-slate-600 font-light">
              <li>Via your Obule account dashboard under the &quot;Orders&quot; or &quot;Tickets&quot; section.</li>
              <li>By contacting the organizer directly using the contact details provided on the event page.</li>
              <li>By reaching out to Obule support with your order reference and reason for the request.</li>
            </ul>
            <p className="mt-3 text-base text-slate-600 leading-relaxed font-light">
              We may ask for additional information to verify your identity and your entitlement to
              a refund. Requests must typically be submitted before the event start time or within
              any deadlines specified by the organizer&apos;s policy.
            </p>
          </section>

          {/* 5. Processing of refunds */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">5. Processing of refunds</h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              When a refund is approved, we aim to process it promptly, but the exact timing can
              vary depending on your bank, card issuer, or payment provider. In most cases, funds
              will be returned to the original payment method. Certain fees (such as non‑refundable
              payment processing fees or previously incurred service charges) may not be included in
              the refund unless required by law or explicitly stated in the organizer&apos;s policy.
            </p>
          </section>

          {/* 6. Non-refundable scenarios */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              6. Non‑refundable purchases
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              Certain categories of tickets or offers may be explicitly marked as non‑refundable.
              This may include last‑minute deals, deeply discounted promotions, or items where the
              organizer has already incurred fixed costs. In such cases, refunds are generally not
              available except where required by law or where the event is cancelled without an
              alternative option.
            </p>
          </section>

          {/* 7. Chargebacks and disputes */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">7. Chargebacks and disputes</h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              If you initiate a chargeback or payment dispute with your bank or card issuer, we may
              be required to provide information about your order and the applicable policies. In
              some cases, a chargeback may result in the automatic cancellation of your ticket or
              access to the event. Repeated or abusive chargebacks may lead to suspension or
              termination of your Obule account in order to protect other users and organizers.
            </p>
          </section>

          {/* 8. Changes to this policy */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              8. Changes to this Refund Policy
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              We may update this Refund Policy from time to time to reflect changes in our Services,
              partnerships, or applicable laws. When we make material changes, we will update the
              effective date and may provide additional notice. Your continued use of Obule after a
              change to this Policy becomes effective means that you accept the updated terms.
            </p>
          </section>
        </div>
      </div>
    </main>
  )
}

export default RefundPolicyPage

